import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import api from "../../api";
import { createAsyncThunkInstance } from "../createAsyncThunkInstance";
import { ICommonInitialState } from "../../shared/types/storeTypes";
import { AxiosError } from "axios";
import {
  ISingleCustomer,
  IUpdateCustomerBody,
  CustomersRequest,
  CustomersResponse,
  CustomerCreateRequest,
  CustomerCreateResponse,
  CustomerViewRequest,
  CustomerViewResponse,
} from "../../api/customers/types";

interface ICustomersState extends ICommonInitialState {
  customers: CustomersResponse;
  currentCustomer: CustomerViewResponse | null;
}

const initialState: ICustomersState = {
  customers: {count: 0, items: [], can_add: false},
  currentCustomer: null,
  isLoading: false,
  error: null,
};

export const fetchCustomers = createAsyncThunkInstance<
  CustomersResponse,
  CustomersRequest | undefined
>("customers/fetchCustomers", async (params?: CustomersRequest) => {
  const response = await api.customers.customers({params: {
    filters: {
      ...params?.params?.filters,
    }
  }});
  return response;
});

export const fetchSingleCustomer = createAsyncThunkInstance<
  CustomerViewResponse,
  CustomerViewRequest
>("customers/fetchSingleCustomer", ({params: {id}}: CustomerViewRequest) => 
  api.customers.customerView({params: {id, expand: ["name", "description", "business_area", "responsible_user", "created_at", "created_by", "comments", "activities", "projects.description", "projects.status", "projects.responsible_user", "can_edit", "can_remove", "applications.name", "applications.status", "applications.project", "applications.customer", "applications.created_at", "applications.created_by", "applications.updated_at", "applications.profile", "applications.location", "applications.candidate_grade", "applications.job_format", "applications.attraction_period", "applications.purchase_rate_max", "applications.marginality"]}})
);


export const fetchCreateCustomer = createAsyncThunk<
  CustomerCreateResponse,
  { params: CustomerCreateRequest },
  { rejectValue: AxiosError }
>("customers/fetchCreateCustomer", async ({ params }, { rejectWithValue }) => {
  try {
    const { data } = await api.customers.customerCreate(params);
    return data;
  } catch (error: unknown) {
    return rejectWithValue(error as AxiosError);
  }
});

export const fetchUpdateCustomer = createAsyncThunk<
  ISingleCustomer,
  { params: IUpdateCustomerBody; id: number },
  { rejectValue: AxiosError }
>(
  "customers/fetchUpdateCustomer",
  async ({ params, id }, { rejectWithValue }) => {
    try {
      const { data } = await api.customers.updateCustomer(id, params);
      return data;
    } catch (error: unknown) {
      return rejectWithValue(error as AxiosError);
    }
  }
);

export const fetchDeleteCustomer = createAsyncThunkInstance(
  "customers/fetchDeleteCustomer",
  api.customers.deleteCustomer
);

const customersSlice = createSlice({
  name: "customers",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchCustomers.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchCustomers.fulfilled, (state, action) => {
        state.isLoading = false;
        state.customers = action.payload;
      })
      .addCase(fetchSingleCustomer.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchSingleCustomer.fulfilled, (state, action) => {
        state.isLoading = false;
        state.currentCustomer = action.payload;
      });
  },
});

export const customersReducer = customersSlice.reducer;
